import logoUrl from "../assets/img/qubit.svg";
import { DESKTOP_QUERY } from "./modules/constants";
import { applyLogoHref } from "./modules/logo";
import { setupPanels } from "./modules/panels";
import { setupSearch } from "./modules/search";
import { setupDrawer } from "./modules/drawer";
import { setupCopyToClipboard } from "./modules/copy-to-clipboard";
import { setupThemeToggle } from "./modules/theme-toggle";
import { gsap } from "gsap";
import { ScrambleTextPlugin } from "gsap/ScrambleTextPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import heroVideo from "../assets/video/3273637-hd_1920_1080_30fps.mp4";
import tech1 from "../assets/img/tech1.webp";
import tech2 from "../assets/img/tech2.webp";
import tech3 from "../assets/img/tech3.webp";
import tech4 from "../assets/img/tech4.webp";
import tech5 from "../assets/img/tech5.webp";
import tech6 from "../assets/img/tech6.webp";
import tech7 from "../assets/img/tech7.webp";
import arImmersive from "../assets/img/material/immersive-experience.webp";
import arRemote from "../assets/img/material/remote-expert.webp";
import lockChemical from "../assets/img/material/chemical-plants.webp";
import lockMining from "../assets/img/material/mining.webp";
import lockRefinery from "../assets/img/material/refineries.webp";

gsap.registerPlugin(ScrambleTextPlugin, ScrollTrigger);

const parseDelayValue = (value) => {
  if (!value) return 0;
  const numeric = Number.parseFloat(value);
  if (Number.isNaN(numeric)) return 0;
  return value.includes("ms") ? numeric / 1000 : numeric;
};

const resolveSlideOffset = (element, index, magnitude) => {
  const from = element?.dataset?.slideFrom;
  if (from === "right") return magnitude;
  if (from === "left") return -magnitude;
  return index % 2 === 0 ? -magnitude : magnitude;
};

const revealWithSlide = (items, options = {}) => {
  if (!items.length) return;
  const { offsetX = 24, offsetY = 12, onComplete } = options;

  gsap.set(items, {
    autoAlpha: 0,
    x: (index, element) => resolveSlideOffset(element, index, offsetX),
    y: offsetY,
    filter: "blur(4px)",
  });

  items.forEach((item) => {
    const delay = parseDelayValue(item.getAttribute("data-delay"));
    gsap.to(item, {
      autoAlpha: 1,
      x: 0,
      y: 0,
      filter: "blur(0px)",
      duration: 0.85,
      ease: "power2.out",
      delay,
      scrollTrigger: {
        trigger: item,
        start: "top 85%",
        once: true,
      },
      onComplete: onComplete ? () => onComplete(item) : undefined,
    });
  });
};

// --- Navigation: mega menu, drawer parity, and panel/search orchestration
const initNav = () => {
  const nav = document.querySelector(".mega-nav");
  if (!nav) return;

  const logoUse = nav.querySelector("#logo-use");
  const panelWrap = nav.querySelector(".mega-nav__panel-wrap");
  const navLinkList = nav.querySelector(".mega-nav__link-list");
  const drawer = document.getElementById("mobile-drawer");
  const drawerSections = drawer
    ? Array.from(drawer.querySelectorAll(".drawer__accordion"))
    : [];
  const desktopQuery = window.matchMedia(DESKTOP_QUERY);
  const balanceThreshold = 1440; // px width where we only rebalance if it actually overflows
  const state = { activePanel: null };
  const isTouchNav =
    window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window;
  const navItems =
    navLinkList && Array.from(navLinkList.children).map((el, idx) => {
      el.dataset.order = el.dataset.order || String(idx);
      return el;
    });

  // Scramble the mega-menu panel links on open/focus
  const runScramble = (link) => {
    const finalText = link?.dataset.scrambleText || link?.textContent?.trim();
    if (!link || !finalText) return;
    if (link.dataset.scrambling === "true") return;
    link.dataset.scrambling = "true";
    gsap.killTweensOf(link);
    link.textContent = "";
    gsap.to(link, {
      duration: 1,
      scrambleText: {
        text: finalText,
        chars: "upperAndLowerCase",
        revealDelay: 0.15,
        speed: 0.35,
      },
      ease: "none",
      onComplete: () => {
        link.dataset.scrambling = "false";
      },
    });
  };

  const initScrambleLinks = () => {
    const panels = nav.querySelectorAll(".mega-nav__panel");
    const panelLinksByTarget = new Map();

    panels.forEach((panel) => {
      const target = panel.dataset.panel;
      const links = Array.from(panel.querySelectorAll(".mega-nav__panel-link"));
      links.forEach((link) => {
        link.dataset.scrambleText = link.textContent.trim();
      });
      if (target) {
        panelLinksByTarget.set(target, links);
      }
    });

    const triggerForTarget = (target) => {
      const links = panelLinksByTarget.get(target);
      if (!links || !links.length) return;
      links.forEach((link) => runScramble(link));
    };

    const navButtons = nav.querySelectorAll(".mega-nav__link");
    navButtons.forEach((btn) => {
      const target = btn.dataset.target;
      const trigger = () => triggerForTarget(target);
      btn.addEventListener("pointerenter", trigger);
      btn.addEventListener("focus", trigger);
    });
  };

  // Keep drawer accordions hidden until we need to surface items there
  const drawerSectionByTarget = new Map();
  drawerSections.forEach((section, idx) => {
    const toggle = section.querySelector(".drawer__accordion-toggle");
    const target = section.dataset.mobileTarget || toggle?.dataset.mobileTarget;
    if (target) {
      drawerSectionByTarget.set(target, section);
      section.dataset.order = section.dataset.order || String(idx);
    }
    section.classList.add("is-hidden");
  });

  const showDrawerSection = (target) => {
    const section = target ? drawerSectionByTarget.get(target) : null;
    section?.classList.remove("is-hidden");
  };

  const hideDrawerSection = (target) => {
    const section = target ? drawerSectionByTarget.get(target) : null;
    section?.classList.add("is-hidden");
  };

  applyLogoHref(logoUse, logoUrl);
  initScrambleLinks();

  const { setActivePanel } = setupPanels(nav, state, desktopQuery, isTouchNav);
  const { closeSearch } = setupSearch(nav);
  const { closeDrawer } = setupDrawer(nav, desktopQuery);

  // When focusing/clicking into the page body, close any open nav overlays
  const closeOverlays = () => {
    closeSearch();
    closeDrawer();
  };

  const closeOverlaysAndReset = () => {
    closeOverlays();
    setActivePanel(null);
  };

  // Hover close handling (desktop): keep open across nav + panel, close after leaving both
  let hoverCloseTimeout = null;
  const clearHoverClose = () => {
    clearTimeout(hoverCloseTimeout);
  };
  const requestHoverClose = () => {
    clearHoverClose();
    hoverCloseTimeout = setTimeout(() => {
      if (!drawer?.classList.contains("drawer--open")) {
        closeOverlaysAndReset();
      }
    }, 120);
  };

  const isInteractiveArea = (target) =>
    !!target &&
    (nav.contains(target) ||
      panelWrap?.contains(target) ||
      drawer?.contains(target));

  const handleExitNav = (target) => {
    if (!isInteractiveArea(target)) {
      closeOverlaysAndReset();
    }
  };

  document.addEventListener("focusin", (event) =>
    handleExitNav(event.target)
  );
  document.addEventListener("pointerdown", (event) =>
    handleExitNav(event.target)
  );
  if (!isTouchNav) {
    nav.addEventListener("pointerenter", clearHoverClose);
    panelWrap?.addEventListener("pointerenter", clearHoverClose);
    nav.addEventListener("pointerleave", (event) => {
      if (!isInteractiveArea(event.relatedTarget)) {
        requestHoverClose();
      }
    });
    panelWrap?.addEventListener("pointerleave", (event) => {
      if (!isInteractiveArea(event.relatedTarget)) {
        requestHoverClose();
      }
    });
  }
  nav.addEventListener(
    "focusout",
    (event) => {
      const next = event.relatedTarget;
      if (!isInteractiveArea(next)) {
        closeOverlaysAndReset();
      }
    },
    true
  );

  // Escape key closes overlays and reverts panel state
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeOverlaysAndReset();
    }
  });

  const hideNavItemAndShowDrawer = (navItem) => {
    if (!navItem) return;
    const target = navItem.dataset.target;
    navItem.classList.add("is-hidden");
    if (target) showDrawerSection(target);
  };

  const rebalanceNav = () => {
    if (!navLinkList || !navItems) return;
    const isDesktop = desktopQuery.matches;
    const isMobile = !isDesktop;
    const wideDesktop = window.innerWidth >= balanceThreshold;
    const navStyles = window.getComputedStyle(navLinkList);
    const navListHidden =
      navLinkList.clientWidth === 0 || navStyles.display === "none";
    const navGap =
      Number.parseFloat(navStyles.columnGap || navStyles.gap || "0") || 0;

    // Start from a clean slate: all nav items visible for measurement,
    // all drawer sections hidden.
    navItems.forEach((el) => el.classList.remove("is-hidden"));
    drawerSections.forEach((section) => section.classList.add("is-hidden"));

    // Force everything into the drawer on mobile to keep parity with the primary nav.
    if (isMobile) {
      navItems.forEach((item) => hideNavItemAndShowDrawer(item));
      drawerSections.forEach((section) => section.classList.remove("is-hidden"));
      const drawerLinks = drawer?.querySelector(".drawer__links");
      const drawerMeta = drawer?.querySelector(".drawer__meta");
      drawerLinks?.classList.remove("is-hidden");
      drawerMeta?.classList.remove("is-hidden");
      return;
    }

    // If the nav list is collapsed (mobile), surface everything in the drawer
    if (navListHidden) {
      drawerSections.forEach((section) => section.classList.remove("is-hidden"));
      return;
    }

    const navItemWidths = navItems.map((item) => {
      const rect = item.getBoundingClientRect();
      return Math.ceil(rect.width || item.scrollWidth || item.offsetWidth || 0);
    });
    const availableWidth = Math.max(navLinkList.clientWidth - 4, 0); // small buffer

    let used = 0;
    let fitCount = 0;
    navItemWidths.some((width, idx) => {
      const next = width + (idx === 0 ? 0 : navGap);
      if (used + next <= availableWidth) {
        used += next;
        fitCount += 1;
        return false;
      }
      return true;
    });

    // Move overflow items into the drawer (via their accordion sections)
    navItems.forEach((item, idx) => {
      if (idx >= fitCount) {
        hideNavItemAndShowDrawer(item);
      }
    });

    const drawerSearch = drawer?.querySelector(".drawer__search");
    drawerSearch?.classList.remove("is-hidden");

    // Hide drawer accordions/meta when empty on wide screens; keep search visible
    const hasVisibleOverflow = navItems.length > fitCount;
    const shouldHideDrawerContent =
      (wideDesktop || isDesktop) && !hasVisibleOverflow;
    const drawerLinks = drawer?.querySelector(".drawer__links");
    const drawerMeta = drawer?.querySelector(".drawer__meta");
    if (shouldHideDrawerContent) {
      drawerLinks?.classList.add("is-hidden");
      drawerMeta?.classList.add("is-hidden");
    } else {
      drawerLinks?.classList.remove("is-hidden");
      drawerMeta?.classList.remove("is-hidden");
    }
  };

  window.addEventListener("resize", rebalanceNav);
  if (document.fonts?.ready) {
    document.fonts.ready.then(() => rebalanceNav());
  }
  // Initial pass after layout/paint
  requestAnimationFrame(rebalanceNav);
};

// --- Hero: swap in Customizer-provided video if present
const setHeroVideoSource = () => {
  const heroVideoEl = document.querySelector(".hero__video");
  if (!heroVideoEl) return;

  const overrideVideo = heroVideoEl.dataset?.heroVideo;
  if (overrideVideo) {
    heroVideoEl.src = overrideVideo;
  } else if (heroVideo) {
    heroVideoEl.src = heroVideo;
  }
};

// --- Footer logo binding (keeps logo URLs in one place)
const setFooterLogo = () => {
  const footerLogoUse = document.querySelector(".footer__logo-use");
  applyLogoHref(footerLogoUse, logoUrl);
};

// --- Featured grid: hydrate static placeholders with imported assets
const featuredImages = {
  tech1,
  tech2,
  tech3,
  tech4,
  tech5,
  tech6,
  tech7,
};

const pageImages = {
  "ar-immersive": arImmersive,
  "ar-remote": arRemote,
  "lock-chemical": lockChemical,
  "lock-mining": lockMining,
  "lock-refinery": lockRefinery,
};

const bindFeaturedImages = () => {
  const imgs = document.querySelectorAll(".featured__image[data-img]");
  imgs.forEach((img) => {
    const key = img.dataset.img;
    const src = featuredImages[key];
    if (src) {
      img.src = src;
    }
  });
};

const bindPageImages = () => {
  const imgs = document.querySelectorAll("img[data-asset]");
  imgs.forEach((img) => {
    const key = img.dataset.asset;
    const src = pageImages[key];
    if (src) {
      img.src = src;
    }
  });

  const bgTargets = document.querySelectorAll("[data-bg-asset]");
  bgTargets.forEach((target) => {
    const key = target.dataset.bgAsset;
    const src = pageImages[key];
    if (src) {
      target.style.backgroundImage = `url('${src}')`;
    }
  });

  const posterTargets = document.querySelectorAll("video[data-poster-asset]");
  posterTargets.forEach((video) => {
    const key = video.dataset.posterAsset;
    const src = pageImages[key];
    if (src) {
      video.poster = src;
    }
  });
};

// Balance the placeholder card only when it keeps the layout tidy
const evaluateSpareFeaturedCard = () => {
  const grid = document.querySelector(".featured__grid");
  const spare = grid?.querySelector(".featured__card--spare");
  if (!grid || !spare) return;

  spare.classList.remove("is-active");
  spare.setAttribute("aria-hidden", "true");
  const heightWithout = grid.getBoundingClientRect().height;

  spare.classList.add("is-active");
  const heightWith = grid.getBoundingClientRect().height;

  const tolerance = 2;
  const shouldUse = Math.round(heightWith) <= Math.round(heightWithout + tolerance);
  if (shouldUse) {
    spare.setAttribute("aria-hidden", "false");
  } else {
    spare.classList.remove("is-active");
    spare.setAttribute("aria-hidden", "true");
  }
};

const initSpareFeaturedCard = () => {
  const grid = document.querySelector(".featured__grid");
  if (!grid) return;
  const imgs = grid.querySelectorAll(".featured__image");

  evaluateSpareFeaturedCard();

  imgs.forEach((img) => {
    if (img.complete) {
      evaluateSpareFeaturedCard();
    } else {
      img.addEventListener("load", () => evaluateSpareFeaturedCard(), { once: true });
    }
  });

  window.addEventListener("resize", () => {
    window.requestAnimationFrame(evaluateSpareFeaturedCard);
  });

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => evaluateSpareFeaturedCard());
  }
};

const initFrontPage = () => {
  const frontPage = document.querySelector(".front-page");
  if (!frontPage) return;

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  const hero = document.querySelector(".hero");
  if (hero && !prefersReducedMotion) {
    const heroEyebrow = hero.querySelector(".hero__eyebrow");
    const heroTitle = hero.querySelector("h1");
    const heroLead = hero.querySelector(".hero__lede");
    const heroScrim = hero.querySelector(".hero__scrim");

    const heroTimeline = gsap.timeline({
      defaults: { duration: 0.9, ease: "power3.out" },
    });

    heroTimeline.from([heroEyebrow, heroTitle, heroLead], {
      y: 26,
      autoAlpha: 0,
      stagger: 0.14,
    });

    if (heroScrim) {
      heroTimeline.fromTo(
        heroScrim,
        { opacity: 0.2 },
        { opacity: 0.6, duration: 1.2 },
        "-=0.8"
      );
    }
  }

  if (!prefersReducedMotion) {
    const revealItems = Array.from(frontPage.querySelectorAll(".fp-reveal"));
    if (revealItems.length) {
      revealWithSlide(revealItems);
    }

    const floatTargets = Array.from(
      frontPage.querySelectorAll("[data-float]")
    );
    floatTargets.forEach((target, index) => {
      gsap.to(target, {
        y: -8,
        duration: 4 + index,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });
    });

    const parallaxTargets = Array.from(
      frontPage.querySelectorAll("[data-parallax]")
    );
    parallaxTargets.forEach((target) => {
      gsap.to(target, {
        y: -24,
        ease: "none",
        scrollTrigger: {
          trigger: target,
          start: "top bottom",
          end: "bottom top",
          scrub: 0.6,
        },
      });
    });
  }
};

const initAboutPage = () => {
  const aboutPage = document.querySelector(".about-page");
  if (!aboutPage) return;

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  const revealItems = Array.from(aboutPage.querySelectorAll(".reveal"));

  if (prefersReducedMotion) {
    revealItems.forEach((item) => item.classList.add("is-visible"));
    return;
  }

  const hero = aboutPage.querySelector(".about-hero");
  if (hero) {
    const heroEyebrow = hero.querySelector(".about-hero__eyebrow");
    const heroTitle = hero.querySelector(".about-hero__title");
    const heroLead = hero.querySelector(".about-hero__lead");
    const heroPanel = hero.querySelector(".about-hero__panel");
    const heroMedia = hero.querySelector(".about-hero__media");

    const heroTimeline = gsap.timeline({
      defaults: { duration: 0.9, ease: "power3.out" },
    });

    heroTimeline.from([heroEyebrow, heroTitle, heroLead], {
      y: 22,
      autoAlpha: 0,
      stagger: 0.12,
    });

    if (heroPanel) {
      heroTimeline.from(
        heroPanel,
        {
          y: 16,
          autoAlpha: 0,
          duration: 0.8,
        },
        "-=0.25"
      );

      gsap.to(heroPanel, {
        y: -6,
        duration: 3.8,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }

    if (heroMedia) {
      gsap.to(heroMedia, {
        scale: 1.04,
        duration: 10,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }
  }

  if (revealItems.length) {
    revealWithSlide(revealItems, {
      onComplete: (item) => item.classList.add("is-visible"),
    });
  }
};

const initIndustrialIotPage = () => {
  const iotPage = document.querySelector(".iot-page");
  if (!iotPage) return;

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (prefersReducedMotion) {
    return;
  }

  const hero = iotPage.querySelector(".iot-hero");
  if (hero) {
    const heroKicker = hero.querySelector(".iot-kicker");
    const heroTitle = hero.querySelector(".iot-hero__title");
    const heroLead = hero.querySelector(".iot-hero__lead");
    const heroSignals = hero.querySelector(".iot-hero__signals");
    const heroPanel = hero.querySelector(".iot-hero__panel");
    const heroMedia = hero.querySelector(".iot-hero__media");

    const heroTimeline = gsap.timeline({
      defaults: { duration: 0.9, ease: "power3.out" },
    });

    heroTimeline.from([heroKicker, heroTitle, heroLead], {
      y: 22,
      autoAlpha: 0,
      stagger: 0.12,
    });

    if (heroSignals) {
      heroTimeline.from(
        heroSignals,
        {
          y: 16,
          autoAlpha: 0,
          duration: 0.7,
        },
        "-=0.35"
      );
    }

    if (heroPanel) {
      heroTimeline.from(
        heroPanel,
        {
          y: 18,
          autoAlpha: 0,
          duration: 0.8,
        },
        "-=0.35"
      );

      gsap.to(heroPanel, {
        y: -6,
        duration: 4,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }

    if (heroMedia) {
      gsap.to(heroMedia, {
        scale: 1.05,
        duration: 12,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }
  }

  const revealItems = Array.from(iotPage.querySelectorAll(".iot-reveal"));
  const scrollItems =
    hero && revealItems.length
      ? revealItems.filter((item) => !hero.contains(item))
      : revealItems;

  if (scrollItems.length) {
    revealWithSlide(scrollItems, { offsetY: 14 });
  }
};

const initEmbeddedSystemsPage = () => {
  const embeddedPage = document.querySelector(".embedded-page");
  if (!embeddedPage) return;

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (prefersReducedMotion) {
    return;
  }

  const hero = embeddedPage.querySelector(".es-hero");
  if (hero) {
    const heroKicker = hero.querySelector(".es-kicker");
    const heroTitle = hero.querySelector(".es-hero__title");
    const heroLead = hero.querySelector(".es-hero__lead");
    const heroStats = hero.querySelector(".es-hero__stats");
    const heroPanel = hero.querySelector(".es-hero__panel");
    const heroMedia = hero.querySelector(".es-hero__media");

    const heroTimeline = gsap.timeline({
      defaults: { duration: 0.9, ease: "power3.out" },
    });

    heroTimeline.from([heroKicker, heroTitle, heroLead], {
      y: 22,
      autoAlpha: 0,
      stagger: 0.12,
    });

    if (heroStats) {
      heroTimeline.from(
        heroStats,
        { y: 16, autoAlpha: 0, duration: 0.7 },
        "-=0.35"
      );
    }

    if (heroPanel) {
      heroTimeline.from(
        heroPanel,
        { y: 18, autoAlpha: 0, duration: 0.8 },
        "-=0.35"
      );

      gsap.to(heroPanel, {
        y: -6,
        duration: 4,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }

    if (heroMedia) {
      gsap.to(heroMedia, {
        scale: 1.05,
        duration: 12,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }
  }

  const revealItems = Array.from(
    embeddedPage.querySelectorAll(".es-reveal")
  );

  if (revealItems.length) {
    revealWithSlide(revealItems, { offsetY: 14 });
  }
};

const initAugmentedRealityPage = () => {
  const arPage = document.querySelector(".ar-page");
  if (!arPage) return;

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (prefersReducedMotion) {
    return;
  }

  const hero = arPage.querySelector(".ar-hero");
  if (hero) {
    const heroKicker = hero.querySelector(".ar-kicker");
    const heroTitle = hero.querySelector(".ar-hero__title");
    const heroLead = hero.querySelector(".ar-hero__lead");
    const heroMetrics = hero.querySelector(".ar-hero__metrics");
    const heroCard = hero.querySelector(".ar-hero__card");
    const heroMedia = hero.querySelector(".ar-hero__media");

    const heroTimeline = gsap.timeline({
      defaults: { duration: 0.9, ease: "power3.out" },
    });

    heroTimeline.from([heroKicker, heroTitle, heroLead], {
      y: 22,
      autoAlpha: 0,
      stagger: 0.12,
    });

    if (heroMetrics) {
      heroTimeline.from(
        heroMetrics,
        { y: 16, autoAlpha: 0, duration: 0.7 },
        "-=0.35"
      );
    }

    if (heroCard) {
      heroTimeline.from(
        heroCard,
        { y: 18, autoAlpha: 0, duration: 0.8 },
        "-=0.35"
      );

      gsap.to(heroCard, {
        y: -6,
        duration: 4.2,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }

    if (heroMedia) {
      gsap.to(heroMedia, {
        scale: 1.05,
        duration: 12,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }
  }

  const revealItems = Array.from(arPage.querySelectorAll(".ar-reveal"));
  const scrollItems =
    hero && revealItems.length
      ? revealItems.filter((item) => !hero.contains(item))
      : revealItems;

  if (scrollItems.length) {
    revealWithSlide(scrollItems, { offsetY: 14 });
  }
};

const initVirtualRealityPage = () => {
  const vrPage = document.querySelector(".vr-page");
  if (!vrPage) return;

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (prefersReducedMotion) {
    return;
  }

  const hero = vrPage.querySelector(".vr-hero");
  if (hero) {
    const heroKicker = hero.querySelector(".vr-kicker");
    const heroTitle = hero.querySelector(".vr-hero__title");
    const heroLead = hero.querySelector(".vr-hero__lead");
    const heroMetrics = hero.querySelector(".vr-hero__metrics");
    const heroCard = hero.querySelector(".vr-hero__card");
    const heroMedia = hero.querySelector(".vr-hero__media");

    const heroTimeline = gsap.timeline({
      defaults: { duration: 0.9, ease: "power3.out" },
    });

    heroTimeline.from([heroKicker, heroTitle, heroLead], {
      y: 22,
      autoAlpha: 0,
      stagger: 0.12,
    });

    if (heroMetrics) {
      heroTimeline.from(
        heroMetrics,
        { y: 16, autoAlpha: 0, duration: 0.7 },
        "-=0.35"
      );
    }

    if (heroCard) {
      heroTimeline.from(
        heroCard,
        { y: 18, autoAlpha: 0, duration: 0.8 },
        "-=0.35"
      );

      gsap.to(heroCard, {
        y: -6,
        duration: 4.4,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }

    if (heroMedia) {
      gsap.to(heroMedia, {
        scale: 1.05,
        duration: 12,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }
  }

  const revealItems = Array.from(vrPage.querySelectorAll(".vr-reveal"));
  const scrollItems =
    hero && revealItems.length
      ? revealItems.filter((item) => !hero.contains(item))
      : revealItems;

  if (scrollItems.length) {
    revealWithSlide(scrollItems, { offsetY: 14 });
  }
};

const initMixedRealityPage = () => {
  const mrPage = document.querySelector(".mr-page");
  if (!mrPage) return;

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (prefersReducedMotion) {
    return;
  }

  const hero = mrPage.querySelector(".mr-hero");
  if (hero) {
    const heroKicker = hero.querySelector(".mr-kicker");
    const heroTitle = hero.querySelector(".mr-hero__title");
    const heroLead = hero.querySelector(".mr-hero__lead");
    const heroMetrics = hero.querySelector(".mr-hero__metrics");
    const heroCard = hero.querySelector(".mr-hero__card");
    const heroMedia = hero.querySelector(".mr-hero__media");

    const heroTimeline = gsap.timeline({
      defaults: { duration: 0.9, ease: "power3.out" },
    });

    heroTimeline.from([heroKicker, heroTitle, heroLead], {
      y: 22,
      autoAlpha: 0,
      stagger: 0.12,
    });

    if (heroMetrics) {
      heroTimeline.from(
        heroMetrics,
        { y: 16, autoAlpha: 0, duration: 0.7 },
        "-=0.35"
      );
    }

    if (heroCard) {
      heroTimeline.from(
        heroCard,
        { y: 18, autoAlpha: 0, duration: 0.8 },
        "-=0.35"
      );

      gsap.to(heroCard, {
        y: -6,
        duration: 4.2,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }

    if (heroMedia) {
      gsap.to(heroMedia, {
        scale: 1.05,
        duration: 12,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }
  }

  const revealItems = Array.from(mrPage.querySelectorAll(".mr-reveal"));
  const scrollItems =
    hero && revealItems.length
      ? revealItems.filter((item) => !hero.contains(item))
      : revealItems;

  if (scrollItems.length) {
    revealWithSlide(scrollItems, { offsetY: 14 });
  }
};

const initExplosionProofLockPage = () => {
  const lockPage = document.querySelector(".lock-page");
  if (!lockPage) return;

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (prefersReducedMotion) {
    return;
  }

  const hero = lockPage.querySelector(".lock-hero");
  if (hero) {
    const heroKicker = hero.querySelector(".lock-kicker");
    const heroTitle = hero.querySelector(".lock-hero__title");
    const heroLead = hero.querySelector(".lock-hero__lead");
    const heroStats = hero.querySelector(".lock-hero__stats");
    const heroCard = hero.querySelector(".lock-hero__card");
    const heroMedia = hero.querySelector(".lock-hero__media");
    const heroVideo = hero.querySelector(".lock-hero__video");

    const heroTimeline = gsap.timeline({
      defaults: { duration: 0.9, ease: "power3.out" },
    });

    heroTimeline.from([heroKicker, heroTitle, heroLead], {
      y: 22,
      autoAlpha: 0,
      stagger: 0.12,
    });

    if (heroStats) {
      heroTimeline.from(
        heroStats,
        { y: 16, autoAlpha: 0, duration: 0.7 },
        "-=0.35"
      );
    }

    if (heroCard) {
      heroTimeline.from(
        heroCard,
        { y: 18, autoAlpha: 0, duration: 0.8 },
        "-=0.35"
      );

      gsap.to(heroCard, {
        y: -6,
        duration: 4.2,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }

    if (heroMedia) {
      gsap.to(heroMedia, {
        scale: 1.05,
        duration: 12,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }

    if (heroVideo) {
      gsap.to(heroVideo, {
        opacity: 0.35,
        duration: 1.4,
        ease: "power1.out",
      });
    }
  }

  const revealItems = Array.from(lockPage.querySelectorAll(".lock-reveal"));
  const scrollItems =
    hero && revealItems.length
      ? revealItems.filter((item) => !hero.contains(item))
      : revealItems;

  if (scrollItems.length) {
    revealWithSlide(scrollItems, { offsetY: 14 });
  }
};

// --- Init order
initNav();
setHeroVideoSource();
setFooterLogo();
bindFeaturedImages();
bindPageImages();
document.addEventListener("DOMContentLoaded", bindFeaturedImages);
document.addEventListener("DOMContentLoaded", bindPageImages);
initSpareFeaturedCard();
initFrontPage();
initAboutPage();
initIndustrialIotPage();
initEmbeddedSystemsPage();
initAugmentedRealityPage();
initVirtualRealityPage();
initMixedRealityPage();
initExplosionProofLockPage();

// --- Pipeline step progress animation
const initPipelineProgress = () => {
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (prefersReducedMotion) return;

  // Find all pipeline step containers (ar, vr, mr, lock prefixes)
  const stepSelectors = [
    ".ar-pipeline__step",
    ".vr-pipeline__step",
    ".mr-pipeline__step",
    ".lock-pipeline__step",
  ];
  const steps = document.querySelectorAll(stepSelectors.join(", "));
  if (!steps.length) return;

  steps.forEach((step, index) => {
    // Scroll-triggered reveal with accent border highlight
    gsap.fromTo(
      step,
      {
        opacity: 0.4,
        borderColor: "transparent",
      },
      {
        opacity: 1,
        borderColor: "rgba(var(--color-primary-rgb), 0.4)",
        duration: 0.5,
        scrollTrigger: {
          trigger: step,
          start: "top 80%",
          end: "top 50%",
          scrub: 0.4,
        },
      }
    );
  });
};
initPipelineProgress();

// --- Copy-to-clipboard + Theme toggle
setupCopyToClipboard();
setupThemeToggle();

// --- Scroll progress bar
const scrollProgressBar = document.querySelector(".scroll-progress__bar");
if (scrollProgressBar) {
  const updateProgress = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0;
    scrollProgressBar.style.transform = `scaleX(${progress})`;
  };
  window.addEventListener("scroll", updateProgress, { passive: true });
  updateProgress();
}

// --- Sticky nav scroll state
const megaNav = document.querySelector(".mega-nav");
if (megaNav) {
  let scrolled = false;
  const onNavScroll = () => {
    const shouldScroll = window.scrollY > 10;
    if (shouldScroll !== scrolled) {
      scrolled = shouldScroll;
      megaNav.classList.toggle("mega-nav--scrolled", scrolled);
    }
  };
  window.addEventListener("scroll", onNavScroll, { passive: true });
  onNavScroll();
}

// --- Back-to-top button
const backToTop = document.querySelector(".back-to-top");
if (backToTop) {
  let visible = false;
  const onBackToTopScroll = () => {
    const shouldShow = window.scrollY > window.innerHeight;
    if (shouldShow !== visible) {
      visible = shouldShow;
      backToTop.classList.toggle("back-to-top--visible", visible);
    }
  };
  window.addEventListener("scroll", onBackToTopScroll, { passive: true });
  onBackToTopScroll();
  backToTop.addEventListener("click", () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  });
}
